import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import {
  adminListLandingPages, adminCreateLandingPage, adminUpdateLandingPage, adminDeleteLandingPage,
  adminListProducts, adminUploadImage,
} from "@/lib/api";
import AdminHeader from "@/components/admin/AdminHeader";

const slugify = (s) => (s || "").toLowerCase().trim()
  .replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");

const assetUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

const emptyForm = {
  id: null, type: "budget", title: "", slug: "", footer_label: "",
  hero_title: "", intro: "", hero_image: null, product_slugs: [],
  seo_title: "", meta_description: "", og_image: null,
  status: "draft", sort_order: 100, publish_date: "",
};

export default function AdminLandingPagesPage() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoadingItems(true);
    try {
      const [lps, prods] = await Promise.all([adminListLandingPages(), adminListProducts()]);
      setItems(lps); setProducts(prods);
    } catch { toast.error("Failed to load landing pages"); }
    setLoadingItems(false);
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
    else if (user) load();
  }, [user, loading, navigate, load]);

  const startCreate = () => setEditing({ ...emptyForm });
  const startEdit = (a) => setEditing({
    ...emptyForm, ...a,
    publish_date: a.publish_date ? a.publish_date.slice(0, 10) : "",
    seo_title: a.seo_title || "", meta_description: a.meta_description || "",
    product_slugs: a.product_slugs || [],
  });
  const cancel = () => setEditing(null);

  const doSave = async (statusOverride) => {
    if (!editing.title.trim()) { toast.error("Title is required"); return; }
    if (!editing.hero_title.trim()) { toast.error("Hero title is required"); return; }
    if (!editing.footer_label.trim()) { toast.error("Footer label is required"); return; }
    const payload = {
      type: editing.type,
      title: editing.title.trim(),
      slug: (editing.slug || slugify(editing.title)).trim(),
      footer_label: editing.footer_label.trim(),
      hero_title: editing.hero_title.trim(),
      intro: editing.intro || "",
      hero_image: editing.hero_image || null,
      product_slugs: (editing.product_slugs || []).filter(Boolean),
      faqs: [],
      seo_title: editing.seo_title || null,
      meta_description: editing.meta_description || null,
      og_image: editing.og_image || null,
      status: statusOverride || editing.status || "draft",
      sort_order: Number(editing.sort_order) || 100,
      publish_date: editing.publish_date ? new Date(editing.publish_date).toISOString() : null,
    };
    setSaving(true);
    try {
      if (editing.id) await adminUpdateLandingPage(editing.id, payload);
      else await adminCreateLandingPage(payload);
      toast.success(editing.id ? "Landing page updated" : "Landing page created");
      setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const onDelete = async (a) => {
    if (!window.confirm(`Delete landing page "${a.title}"?`)) return;
    try { await adminDeleteLandingPage(a.id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const preview = (a) => window.open(`/collections/${a.slug}`, "_blank");

  const uploadOg = async (file) => {
    if (!file) return;
    try {
      const { url } = await adminUploadImage(file);
      setEditing((prev) => ({ ...prev, og_image: url }));
    } catch { toast.error(`Upload failed`); }
  };

  const uploadHero = async (file) => {
    if (!file) return;
    try {
      const { url } = await adminUploadImage(file);
      setEditing((prev) => ({ ...prev, hero_image: url }));
    } catch { toast.error(`Upload failed`); }
  };

  if (loading || !user) return <div className="admin-shell"><div className="admin-loading">Loading…</div></div>;

  return (
    <div className="admin-shell utsah-app" data-testid="admin-landing-pages">
      <AdminHeader active="landing-pages" onLogout={async () => { await logout(); navigate("/admin/login"); }} email={user.email} />
      <div className="admin-container">
        <div className="admin-heading-row">
          <div>
            <div className="eyebrow">SEO</div>
            <h1 className="admin-heading">Landing Pages</h1>
          </div>
          {!editing && (
            <button className="admin-export-btn" onClick={startCreate} data-testid="admin-new-lp-btn">+ New Landing Page</button>
          )}
        </div>

        {editing ? (
          <LandingForm
            form={editing}
            products={products}
            saving={saving}
            onChange={setEditing}
            onSave={doSave}
            onCancel={cancel}
            onPreview={editing.id ? () => preview(editing) : null}
            onUploadOg={uploadOg}
            onUploadHero={uploadHero}
          />
        ) : (
          <div className="admin-table-wrap" data-testid="admin-lp-table">
            {loadingItems ? (
              <div className="admin-loading">Loading…</div>
            ) : items.length === 0 ? (
              <div className="admin-empty">No landing pages yet. Click "New Landing Page" to create one.</div>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr><th>Title</th><th>Type</th><th>Footer Label</th><th>Slug</th><th>Status</th><th className="admin-th-actions">Actions</th></tr>
                </thead>
                <tbody>
                  {items.map((a) => (
                    <tr key={a.id} data-testid={`lp-row-${a.slug}`}>
                      <td><strong>{a.title}</strong></td>
                      <td><span className="admin-badge">{a.type}</span></td>
                      <td>{a.footer_label}</td>
                      <td style={{ opacity: 0.6, fontSize: 12 }}>/collections/{a.slug}</td>
                      <td><span className={`admin-badge admin-badge-${a.status}`}>{a.status}</span></td>
                      <td className="admin-td-actions">
                        <button className="admin-link-btn" onClick={() => startEdit(a)} data-testid={`lp-edit-${a.slug}`}>Edit</button>
                        <button className="admin-link-btn" onClick={() => preview(a)} data-testid={`lp-preview-${a.slug}`}>Preview</button>
                        <button className="admin-link-btn admin-link-danger" onClick={() => onDelete(a)} data-testid={`lp-del-${a.slug}`}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
      <Toaster position="bottom-center" richColors theme="light" />
    </div>
  );
}

function LandingForm({ form, products, saving, onChange, onSave, onCancel, onPreview, onUploadOg, onUploadHero }) {
  const set = (k, v) => onChange({ ...form, [k]: v });

  const toggleProduct = (slug) => {
    const arr = form.product_slugs || [];
    if (arr.includes(slug)) set("product_slugs", arr.filter((s) => s !== slug));
    else set("product_slugs", [...arr, slug]);
  };
  const moveProduct = (from, to) => {
    if (to < 0 || to >= form.product_slugs.length) return;
    const arr = [...form.product_slugs]; const [x] = arr.splice(from, 1); arr.splice(to, 0, x);
    set("product_slugs", arr);
  };

  const bySlug = new Map(products.map((p) => [p.slug, p]));

  return (
    <div className="admin-form" data-testid="admin-lp-form">
      <FormSection title="1 · Type & Title">
        <div className="admin-form-row">
          <label>Type<span className="req">*</span></label>
          <select value={form.type} onChange={(e) => set("type", e.target.value)} data-testid="lp-form-type">
            <option value="budget">Budget</option>
            <option value="city">City</option>
          </select>
          <div className="admin-form-hint">Determines which footer column this page appears in.</div>
        </div>
        <div className="admin-form-row">
          <label>Title (SEO H1 fallback)<span className="req">*</span></label>
          <input type="text" value={form.title}
            onChange={(e) => set("title", e.target.value)}
            onBlur={() => { if (!form.slug) set("slug", slugify(form.title)); }}
            placeholder="e.g. Diwali Corporate Gifts Under ₹1,500"
            data-testid="lp-form-title" />
        </div>
        <div className="admin-form-row">
          <label>URL Slug<span className="req">*</span></label>
          <input type="text" value={form.slug}
            onChange={(e) => set("slug", slugify(e.target.value))}
            placeholder="e.g. diwali-corporate-gifts-under-1500"
            data-testid="lp-form-slug" />
          <div className="admin-form-hint">Public URL: /collections/{form.slug || "your-slug"}</div>
        </div>
        <div className="admin-form-row">
          <label>Footer Label<span className="req">*</span></label>
          <input type="text" value={form.footer_label}
            onChange={(e) => set("footer_label", e.target.value)}
            placeholder="e.g. Under ₹1,500 or Mumbai"
            data-testid="lp-form-footer-label" />
          <div className="admin-form-hint">Short label used in the footer link.</div>
        </div>
        <div className="admin-form-row">
          <label>Sort Order (lower = higher in footer)</label>
          <input type="number" min={0} value={form.sort_order}
            onChange={(e) => set("sort_order", e.target.value)}
            data-testid="lp-form-sort-order" />
        </div>
      </FormSection>

      <FormSection title="2 · Hero">
        <div className="admin-form-row">
          <label>Hero Banner Image<span className="req">*</span></label>
          <input type="file" accept="image/*" onChange={(e) => onUploadHero(e.target.files?.[0])}
            data-testid="lp-form-hero-upload" />
          {form.hero_image && (
            <div className="admin-image-tile" style={{ maxWidth: 320, marginTop: 12 }}>
              <img src={assetUrl(form.hero_image)} alt="" />
              <div className="admin-image-controls">
                <button type="button" className="admin-link-danger" onClick={() => set("hero_image", null)}>Remove</button>
              </div>
            </div>
          )}
          <div className="admin-form-hint">Displayed as the banner at the top of the landing page. Separate from Open Graph Image (which is used only for SEO/social).</div>
        </div>
        <div className="admin-form-row">
          <label>Hero Title (H1)<span className="req">*</span></label>
          <input type="text" value={form.hero_title}
            onChange={(e) => set("hero_title", e.target.value)}
            placeholder="e.g. Premium Diwali Corporate Gifts Under ₹1,500"
            data-testid="lp-form-hero" />
        </div>
        <div className="admin-form-row">
          <label>Short Introduction</label>
          <textarea rows={4} value={form.intro}
            onChange={(e) => set("intro", e.target.value)}
            data-testid="lp-form-intro" />
        </div>
      </FormSection>

      <FormSection title="3 · Products (pick from Product CMS)">
        <div className="admin-form-hint" style={{ marginBottom: 10 }}>
          Selected {form.product_slugs.length} product(s). Selection order = display order.
        </div>
        {form.product_slugs.length > 0 && (
          <ol style={{ margin: "0 0 16px 20px", padding: 0 }} data-testid="lp-form-selected-products">
            {form.product_slugs.map((s, i) => {
              const p = bySlug.get(s);
              return (
                <li key={s} className="admin-repeater-row" style={{ marginBottom: 6 }}>
                  <div style={{ flex: 1 }}>{p ? p.name : s} <span style={{ opacity: 0.5, fontSize: 12 }}>({s})</span></div>
                  <div className="admin-repeater-controls">
                    <button type="button" onClick={() => moveProduct(i, i - 1)} disabled={i === 0}>↑</button>
                    <button type="button" onClick={() => moveProduct(i, i + 1)} disabled={i === form.product_slugs.length - 1}>↓</button>
                    <button type="button" className="admin-link-danger" onClick={() => toggleProduct(s)}>×</button>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
        <div className="admin-form-hint" style={{ marginTop: 10 }}>Add / remove products:</div>
        <div className="admin-chip-row" style={{ marginTop: 8 }}>
          {products.map((p) => (
            <label key={p.slug} className={`admin-chip ${form.product_slugs.includes(p.slug) ? "active" : ""}`}>
              <input type="checkbox"
                checked={form.product_slugs.includes(p.slug)}
                onChange={() => toggleProduct(p.slug)}
                data-testid={`lp-form-prod-${p.slug}`} />
              {p.name}
              <span style={{ marginLeft: 6, opacity: 0.6, fontSize: 11 }}>₹{Number(p.price || 0).toLocaleString("en-IN")}</span>
            </label>
          ))}
        </div>
      </FormSection>

      <FormSection title="4 · SEO (Optional)">
        <div className="admin-form-row">
          <label>SEO Title</label>
          <input type="text" value={form.seo_title}
            onChange={(e) => set("seo_title", e.target.value)}
            placeholder={`Auto: ${form.title || "Title"} | Utsah`}
            data-testid="lp-form-seo-title" />
        </div>
        <div className="admin-form-row">
          <label>Meta Description</label>
          <textarea rows={2} value={form.meta_description}
            onChange={(e) => set("meta_description", e.target.value)}
            placeholder="Auto: uses the short introduction if left blank"
            data-testid="lp-form-meta-desc" />
        </div>
        <div className="admin-form-row">
          <label>Open Graph Image (Optional)</label>
          <input type="file" accept="image/*" onChange={(e) => onUploadOg(e.target.files?.[0])}
            data-testid="lp-form-og-upload" />
          {form.og_image && (
            <div className="admin-image-tile" style={{ maxWidth: 240, marginTop: 12 }}>
              <img src={assetUrl(form.og_image)} alt="" />
              <div className="admin-image-controls">
                <button type="button" className="admin-link-danger" onClick={() => set("og_image", null)}>Remove</button>
              </div>
            </div>
          )}
          <div className="admin-form-hint">If empty, the first selected product image is used for social sharing.</div>
        </div>
      </FormSection>

      <FormSection title="5 · Publishing">
        <div className="admin-form-row">
          <label>Publish Date</label>
          <input type="date" value={form.publish_date || ""}
            onChange={(e) => set("publish_date", e.target.value)}
            data-testid="lp-form-publish-date" />
          <div className="admin-form-hint">Leave blank to auto-fill on publish.</div>
        </div>
        <div className="admin-form-row">
          <label>Status</label>
          <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="lp-form-status">
            <option value="draft">Draft (hidden)</option>
            <option value="published">Published (live)</option>
          </select>
        </div>
      </FormSection>

      <div className="admin-form-actions" data-testid="lp-form-actions">
        <button className="admin-nav-btn" onClick={onCancel} disabled={saving} data-testid="lp-form-cancel">Cancel</button>
        {onPreview && (
          <button className="admin-nav-btn" onClick={onPreview} disabled={saving} data-testid="lp-form-preview">Preview</button>
        )}
        <button className="admin-nav-btn" onClick={() => onSave("draft")} disabled={saving} data-testid="lp-form-save-draft">Save Draft</button>
        <button className="admin-export-btn" onClick={() => onSave("published")} disabled={saving} data-testid="lp-form-publish">
          {saving ? "Saving…" : "Publish"}
        </button>
      </div>
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <section className="admin-form-section">
      <h3 className="admin-form-section-title">{title}</h3>
      {children}
    </section>
  );
}
